// JSON Validator & Normalizer
// Coerces compatible types instead of rejecting them.
// Only returns errors for truly unrecoverable issues.

const coerce = (value, expected) => {
    if (typeof value === expected) return { value, ok: true };
    if (expected === 'string') return { value: String(value), ok: true };
    if (expected === 'number') {
        const n = Number(value);
        if (!isNaN(n)) return { value: n, ok: true };
    }
    return { value, ok: false };
};

const defaultSettings = {
    display_type: 'map',
    map_display_settings: {
        center: { lat: 0, lng: 0 },
        zoom: 13,
        mapId: '9cc7d3e49ca28f32',
    },
    image_display_settings: { image_url: '' },
    dashboard: { location: '', temperature: '' },
};

/**
 * Normalize settings from various formats into the expected shape.
 * Handles flat settings (center/zoom at root) and nested formats.
 */
const normalizeSettings = (settings) => {
    if (!settings || typeof settings !== 'object') return { ...defaultSettings };

    const result = { ...defaultSettings };

    // Handle display_type
    if (settings.display_type === 'map' || settings.display_type === 'image') {
        result.display_type = settings.display_type;
    }

    // Handle map_display_settings — could be nested or flat
    if (settings.map_display_settings) {
        result.map_display_settings = {
            ...defaultSettings.map_display_settings,
            ...settings.map_display_settings,
        };
    } else if (settings.center) {
        // Flat format: { center, zoom, mapId } at settings root
        result.map_display_settings = {
            center: settings.center,
            zoom: settings.zoom || 13,
            mapId: settings.mapId || defaultSettings.map_display_settings.mapId,
        };
    }

    // Handle image_display_settings
    if (settings.image_display_settings) {
        result.image_display_settings = {
            ...defaultSettings.image_display_settings,
            ...settings.image_display_settings,
        };
    }

    // Handle dashboard
    if (settings.dashboard) {
        result.dashboard = {
            ...defaultSettings.dashboard,
            ...settings.dashboard,
        };
    }

    return result;
};

/**
 * Recursively normalize an object against a template.
 * Coerces types where possible, fills defaults for missing optional fields.
 */
const normalizeObject = (obj, template, path = '', errors = []) => {
    if (!obj || typeof obj !== 'object') return obj;

    for (const key in template) {
        const currentPath = path ? `${path}.${key}` : key;
        const expected = template[key];

        if (!(key in obj)) {
            // Missing property — skip, the app can handle undefined
            continue;
        }

        const value = obj[key];

        if (typeof expected === 'string') {
            // Primitive type — coerce if needed
            const result = coerce(value, expected);
            if (result.ok) {
                obj[key] = result.value;
            }
            // Don't error — just leave the value as-is if coercion fails
        } else if (Array.isArray(expected)) {
            if (!Array.isArray(value)) continue;
            if (expected.length > 0 && typeof expected[0] === 'object') {
                value.forEach((item, index) => {
                    if (typeof item === 'object' && item !== null) {
                        normalizeObject(item, expected[0], `${currentPath}[${index}]`, errors);
                    }
                });
            }
        } else if (typeof expected === 'object') {
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                normalizeObject(value, expected, currentPath, errors);
            }
        }
    }

    return obj;
};

const dataTemplate = {
    tasks: [
        {
            id: 'number',
            status: 'string',
            aiScore: 'number',
            object: 'string',
            initiatedBy: 'string',
            missionType: 'string',
            dueDate: 'string',
            priority: 'string',
            responsibleTeam: 'string',
            device_id: 'string',
            activities: [
                { description: 'string', date: 'string', status: 'string' },
            ],
        },
    ],
    devices: [
        {
            lat: 'number',
            lng: 'number',
            status: 'number',
            sensor_id: 'string',
            battery: 'number',
            signal: 'number',
            district: 'string',
            location: 'number',
            type: 'string',
            measures: [
                {
                    date: 'string',
                    image: 'string',
                    ai_score: 'string',
                    readings: [
                        { key: 'string', value: 'string', status: 'string' },
                    ],
                },
            ],
            maintenances: [
                { date: 'string', task: 'string', team: 'string' },
            ],
        },
    ],
    districts: [
        { id: 'string', image: 'string', lat: 'number', lng: 'number', name: 'string' },
    ],
};

const validateJsonStructure = (json) => {
    const errors = [];

    // Must have at minimum devices and districts
    if (!json || typeof json !== 'object') {
        return ['Invalid JSON: not an object'];
    }
    if (!Array.isArray(json.devices) || json.devices.length === 0) {
        errors.push('Missing or empty "devices" array');
    }
    if (!Array.isArray(json.districts) || json.districts.length === 0) {
        errors.push('Missing or empty "districts" array');
    }

    if (errors.length > 0) return errors;

    // Normalize settings (handles flat and nested formats)
    json.settings = normalizeSettings(json.settings);

    // Ensure optional top-level arrays exist
    if (!Array.isArray(json.tasks)) json.tasks = [];
    if (!json.images) json.images = { districts: [], devices: [] };

    // Coerce types throughout the data (mutates in place)
    normalizeObject(json, dataTemplate, '', errors);

    return errors;
};

export { normalizeSettings };
export default validateJsonStructure;
